import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import localforage from "localforage";
import { useLazyQuery, useMutation } from "@apollo/client";

import "./ResetPasswordPage.scss";
import Button from "../../component/button/Button";
import { validatePassword } from "../../service/utils/functions";
import { logout } from "../../service/graphql/utils";
import {
  GET_USER_PAGE_PERMISSIONS,
  RESET_PASSWORD_MUTATION,
} from "../../service/graphql/auth";
import * as Storage from "../../service/storage";

const ResetPasswordPage = () => {
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [requestCode, setRequestCode] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePwdProgress, setChangePwdProgress] = useState(false);

  // State for live password validation error in reset form
  const [showPasswordRequirementsError, setShowPasswordRequirementsError] =
    useState(false);

  const [wrongPassword, setWrongPassword] = useState(false);
  const [unconfirmedPwd, setUnconfirmedPwd] = useState(false);
  const [genericError, setGenericError] = useState(false);

  const [resetPassword, { data: resetPwdRes, error: resetPwdErr }] =
    useMutation(RESET_PASSWORD_MUTATION, {
      errorPolicy: "all",
    });
  const [getUserPagePermissions] = useLazyQuery(GET_USER_PAGE_PERMISSIONS);

  useEffect(() => {
    const code = urlParams.get("code");
    if (code && code.length > 0) {
      window.history.replaceState(
        null,
        "",
        `/reset-password?${urlParams.toString()}`
      );
      setRequestCode(code);
    } else logout(navigate);
  }, [urlParams, navigate]);

  const resetErrorMsgs = () => {
    setWrongPassword(false);
    setUnconfirmedPwd(false);
    setShowPasswordRequirementsError(false); // Reset live password error state
    setGenericError(false);
  };

  const submitChangePwd = async () => {
    setChangePwdProgress(true);
    if (validatePassword(password)) {
      if (password === confirmPassword) {
        resetPassword({
          variables: {
            code: requestCode,
            password,
            passwordConfirm: confirmPassword,
          },
          context: { apiName: "cms" },
        });
      } else {
        setUnconfirmedPwd(true);
        setChangePwdProgress(false);
      }
    } else {
      //   setWrongPassword(true);
      setShowPasswordRequirementsError(true); // reset password complexity check
      setChangePwdProgress(false);
    }
  };

  useEffect(() => {
    if (resetPwdRes && resetPwdRes.resetPassword) {
      const jwt = resetPwdRes.resetPassword.jwt;
      const u = resetPwdRes.resetPassword.user;
      Storage.TOKEN.set(jwt);
      getUserPagePermissions({
        variables: { id: u.id },
        context: { apiName: "cms" },
      }).then((res: any) => {
        const permissionsTable =
          res.data?.userDetails?.data[0]?.attributes.page_permissions;
        localforage
          .setItem("userData", {
            name: u.username,
            email: u.email,
            page_permissions: permissionsTable,
          })
          .then(() => {
            navigate("/dashboard/home");
          });
      });
    }
  }, [resetPwdRes, navigate]);

  useEffect(() => {
    if (resetPwdErr) {
      console.log(resetPwdErr);
      setGenericError(true);
      setChangePwdProgress(false);
    }
  }, [resetPwdErr, changePwdProgress]);

  return (
    <div className="screen-bg" style={{ backgroundColor: "#122048" }}>
      <video playsInline autoPlay muted loop>
        <source
          src="/images/video_background_login.mp4"
          type="video/mp4"
        ></source>
      </video>
      <div className="reset-pwd-form">
        <h3 className="reset-pwd-title">Reset Password</h3>
        <div className="reset-pwd-credential-block">
          <div className="reset-pwd-inputbox">
            <label htmlFor="reset_pwd">New Password</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="password"
                  id="reset_pwd"
                  name="reset_pwd"
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setPassword(e.target.value);
                    setShowPasswordRequirementsError(
                      e.target.value.length > 0 &&
                        !validatePassword(e.target.value)
                    ); // Reset live password error
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faLock} />
              </div>
              {/* {wrongPassword && <div className='error-msg'> */}
              {showPasswordRequirementsError && (
                <div className="error-msg">
                  Password needs to be between 8 and 20 characters long and must
                  have at least one number and one symbol (@, $, !, %, *, #, ?,
                  &)
                  {/* </div>} */}
                </div>
              )}
            </div>
          </div>
          <div className="reset-pwd-inputbox">
            <label htmlFor="confirm_pwd">Confirm Password</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="password"
                  id="confirm_pwd"
                  name="confirm_pwd"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setConfirmPassword(e.target.value);
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faLock} />
              </div>
              {unconfirmedPwd && !genericError && (
                <div className="error-msg">Passwords must match</div>
              )}
              {genericError && (
                <div className="error-msg">An unexpected error occurred</div>
              )}
            </div>
          </div>
          <Button
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#ffffff",
              color: "#0b0a31",
              width: "54.5%",
              height: "1.4rem",
              borderRadius: "1.4rem",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
            title="Change Password"
            loading={changePwdProgress}
            isLogin
            onClick={submitChangePwd}
          />
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
